import { DT, FIREBALL, SHOCKWAVE, inputChanged, quantizeAim, quantizeMove } from '@blorbs/common';
import type { FireballState, InputMessage, PlayerState, Vec } from '@blorbs/common';
import { Net } from './net';
import { Input } from './input';
import { Hud } from './hud';
import { Renderer } from './render/renderer';
import { GhostFireballs, HitPredictor, Predictor } from './prediction';
import { apiBase, discordIdentity, inDiscord } from './discord';
import { resolveRegion } from './regions';

function showMessage(text: string): void {
  const form = document.getElementById('name-form')!;
  form.replaceChildren(
    Object.assign(document.createElement('h1'), { textContent: 'Blorbs' }),
    Object.assign(document.createElement('p'), { textContent: text }),
  );
  document.getElementById('overlay')!.classList.remove('hidden');
}

async function resolveIdentity(): Promise<{ name: string; room: string }> {
  if (inDiscord()) {
    showMessage('Connecting to Discord…');
    const fromDiscord = await discordIdentity();
    if (fromDiscord) return fromDiscord;
  }

  const params = new URLSearchParams(location.search);
  const room = params.get('room') ?? 'lobby';
  const quickName = params.get('name');
  if (quickName) return { name: quickName, room };

  const form = document.getElementById('name-form') as HTMLFormElement;
  const inputEl = document.getElementById('name-input') as HTMLInputElement;
  inputEl.value = localStorage.getItem('blorbs:name') ?? '';
  inputEl.focus();

  const name = await new Promise<string>((resolve) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const v = inputEl.value.trim() || `Orb${Math.floor(Math.random() * 1000)}`;
      localStorage.setItem('blorbs:name', v);
      resolve(v);
    });
  });
  return { name, room };
}

async function main(): Promise<void> {
  const canvas = document.getElementById('game') as HTMLCanvasElement;
  const renderer = new Renderer(canvas);
  const net = new Net(apiBase());
  net.onDisconnect = (reason) => showMessage(reason);
  const predictor = new Predictor();
  const ghosts = new GhostFireballs();
  const hits = new HitPredictor();
  const input = new Input(canvas, renderer.camera, () => predictor.position());
  const hud = new Hud(input.isTouch, {
    bots: (count, difficulty) => net.setBots(count, difficulty),
    shockwave: () => input.requestShockwave(),
    teleport: () => input.requestTeleportAlongAim(),
  });

  const { name, room } = await resolveIdentity();
  await resolveRegion(apiBase(), room);
  document.getElementById('overlay')!.classList.add('hidden');
  await net.join(room, name);

  const botsParam = Number(new URLSearchParams(location.search).get('bots'));
  if (botsParam > 0) net.setBots(botsParam, hud.difficulty());

  // Fireballs fly in straight lines at a known speed, so we can draw them where
  // the server has them *now* instead of a round trip ago.
  const extrapolate = (f: FireballState): Vec => {
    // Never draw it past where it will fizzle.
    const ahead = Math.min(f.left, (FIREBALL.speed * net.renderLagMs) / 1000);
    return { x: f.x + f.dx * ahead, y: f.y + f.dy * ahead };
  };

  net.onSnapshot((snap) => {
    const me = snap.players.find((p) => p.id === net.myId);
    if (me) predictor.reconcile(me, snap.islandRadius, snap.phase === 'countdown');
    ghosts.onSnapshot(snap, net.myId, (f) => extrapolate(f), performance.now(), (g, r) => renderer.trails.transfer(g, r));
  });

  net.onEvent((e) => {
    const fx = renderer.effects;
    switch (e.type) {
      case 'hit':
        if (!hits.consume(e.fireballId, e.attackerId, net.myId)) fx.hit(e.x, e.y, e.force);
        if (e.targetId === net.myId) fx.addShake(6 + e.force * 12);
        break;
      case 'ko':
        fx.ko(e.x, e.y);
        if (e.targetId === net.myId) fx.addShake(18);
        break;
      case 'bump':
        fx.bump(e.x, e.y, e.force);
        break;
      case 'fizzle':
        fx.fizzle(e.x, e.y);
        break;
      case 'teleport':
        if (e.playerId !== net.myId) fx.teleport({ x: e.fromX, y: e.fromY }, { x: e.toX, y: e.toY }); // ours was shown when predicted
        break;
      case 'shockwave':
        if (e.playerId !== net.myId) fx.shockwave(e.x, e.y, e.radius);
        if (e.hits > 0) fx.addShake(3 + e.hits * 2);
        break;
    }
  });

  // If the tab goes to the background the loop stops; tell the server to stop
  // moving us rather than dead-reckoning the last input forever.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      lastSent = predictor.step({ x: 0, y: 0 }, quantizeAim(input.aim), 0, false, null, net.serverNow).msg;
      net.sendInput(lastSent);
    }
  });

  let last = performance.now();
  let acc = 0;
  let lastSent: InputMessage | null = null;
  /** local (server-time) estimate of when we can shockwave again, so the ring starts instantly */
  let shockwaveLocalUntil = 0;
  const frame = (now: number): void => {
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;
    input.poll();

    const newest = net.newest();
    const frozen = newest?.phase === 'countdown';
    const islandRadius = newest?.islandRadius ?? 0;

    // Fixed 30 Hz simulation steps: sample input, predict, and send only when
    // the input changed (or as a keepalive) — the server repeats the last one.
    // Frame rate is decoupled so a 144 Hz screen and a 30 Hz phone behave alike.
    acc += dt;
    while (acc >= DT) {
      acc -= DT;
      // Consumed inside the step: a request made on a frame with no step waits for the next one.
      const teleport = input.consumeTeleport();
      const { msg, jumpedFrom } = predictor.step(quantizeMove(input.move), quantizeAim(input.aim), islandRadius, frozen, teleport, net.serverNow);
      if (jumpedFrom) {
        const to = predictor.position()!;
        renderer.effects.teleport(jumpedFrom, to);
      }
      if (inputChanged(msg, lastSent)) {
        net.sendInput(msg);
        lastSent = msg;
      }
    }

    const me = predictor.view();
    const shotDir = input.consumeShoot();
    if (shotDir && me?.alive && !frozen) {
      if (ghosts.tryShoot(me, shotDir, now)) net.shoot(shotDir);
    }
    if (input.consumeShockwave() && me?.alive && !frozen) {
      const until = Math.max(me.shockwaveCooldownUntil, shockwaveLocalUntil);
      if (net.serverNow >= until) {
        shockwaveLocalUntil = net.serverNow + SHOCKWAVE.cooldownMs;
        net.shockwave();
        renderer.effects.shockwave(me.x, me.y, SHOCKWAVE.radius); // shown now; the server's event for us is deduped
        renderer.effects.addShake(5);
      }
    }
    input.consumeAnyPress();

    predictor.update(dt);
    ghosts.update(dt, now);

    const view = net.view(now);
    if (view) {
      if (me) {
        // Draw ourselves from the prediction, everyone else interpolated.
        const cooldownLocal = view.serverNow + Math.max(0, ghosts.cooldownUntil - now);
        const mine: PlayerState = {
          ...me,
          cooldownUntil: Math.max(me.cooldownUntil, cooldownLocal),
          shockwaveCooldownUntil: Math.max(me.shockwaveCooldownUntil, shockwaveLocalUntil),
        };
        view.players = view.players.map((p) => (p.id === net.myId ? mine : p));
      }
      hits.prune(view.fireballs);
      const real = view.fireballs.map((f) => {
        const p = extrapolate(f);
        const o = ghosts.offset(f.id, now);
        const left = Math.max(0, f.left - (FIREBALL.speed * net.renderLagMs) / 1000);
        return o ? { ...f, x: p.x + o.x, y: p.y + o.y, left } : { ...f, x: p.x, y: p.y, left };
      });
      const all = [...real, ...ghosts.list()];
      for (const f of hits.check(all, view.players, net.myId, now)) {
        renderer.effects.hit(f.x, f.y, 0.8);
        if (f.id < 0) ghosts.remove(f.id);
      }
      view.fireballs = all.filter((f) => !hits.isHidden(f.id));
      renderer.draw(view, net.myId, input, now / 1000, dt);
      hud.update(view, net.myId);
      if (me) {
        hud.setCooldowns(view.serverNow, {
          fire: Math.max(me.cooldownUntil, view.serverNow + Math.max(0, ghosts.cooldownUntil - now)),
          shock: Math.max(me.shockwaveCooldownUntil, shockwaveLocalUntil),
          tp: me.teleportCooldownUntil,
        });
      }
      hud.setPing(net.rtt, net.rttJitter, net.route);
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

main().catch((err: unknown) => {
  console.error(err);
  showMessage(err instanceof Error ? err.message : 'Something went wrong.');
});
